import { useUserSession } from '@solinkcorp/authentication';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  type AnalyticsDayPartLike,
  type AnalyticsLayoutLike,
  collectLiveDayPartIds,
  getAdoptionSnapshotSignature,
  getLayoutAdoptionMetrics,
  getSDMAdoptionMetrics,
} from '../analytics/sdmAdoptionMetrics';
import { SDM_EVENTS } from '../constants';
import { useSDMAnalytics } from '../contexts/AnalyticsContext';

const lastSignatureByCustomer = new Map<string, string>();

export function resetSDMAdoptionSnapshotDedupe(): void {
  lastSignatureByCustomer.clear();
}

export function useSDMAdoptionSnapshot(
  layouts: ReadonlyArray<AnalyticsLayoutLike> | undefined,
  dayParts: ReadonlyArray<AnalyticsDayPartLike> | undefined,
): void {
  const analytics = useSDMAnalytics();
  const { customerId } = useUserSession();

  React.useEffect(() => {
    if (!layouts || !dayParts) return;
    if (!customerId) return;

    const metrics = getSDMAdoptionMetrics(layouts, dayParts);
    // The live day part ids are passed through so the per-layout rows ignore threshold
    // references to deleted day parts, matching the census.
    const liveDayPartIds = collectLiveDayPartIds(dayParts);
    const layoutMetrics = layouts.map((layout) => getLayoutAdoptionMetrics(layout, liveDayPartIds));

    // Signed over the census *and* the per-layout rows, because both are emitted together and
    // the census alone cannot see a layout rename or a widget reshuffle between layouts.
    const signature = getAdoptionSnapshotSignature(customerId, metrics, layoutMetrics);

    if (lastSignatureByCustomer.get(customerId) === signature) return;
    lastSignatureByCustomer.set(customerId, signature);

    const snapshotId = uuidv4();
    const snapshotAt = new Date().toISOString();

    /** Customer-level adoption census: layout/widget/day part totals for the org. */
    analytics.trackSDMEvent(SDM_EVENTS.ADOPTION_SNAPSHOT, {
      description: 'Service display monitor adoption snapshot for the customer',
      snapshotId,
      snapshotAt,
      ...metrics,
    });

    /** One event per saved layout, reusing the rows the signature was computed over. */
    for (const layoutProperties of layoutMetrics) {
      analytics.trackSDMEvent(SDM_EVENTS.LAYOUT_SNAPSHOT, {
        description: 'Service display monitor layout composition snapshot',
        snapshotId,
        snapshotAt,
        snapshotLayoutCount: layoutMetrics.length,
        ...layoutProperties,
      });
    }
  }, [layouts, dayParts, customerId, analytics]);
}
